
function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

const L_PDP_PREV_COLLECTION_PAGE_KEY = 'l-pdp:prev-collection-page'

function l_normalizePathname(url) {
	if (!url) return ''

	try {
		const parsed = new URL(url, window.location.origin)
		return parsed.pathname.replace(/\/$/, '') || '/'
	} catch (error) {
		return String(url).split('?')[0].replace(/\/$/, '') || '/'
	}
}

function l_isCollectionPagePath(pathname) {
	return /^\/collections\/[^/]+$/.test(l_normalizePathname(pathname))
}

function l_setPdpPrevCollectionPage(title) {
	try {
		sessionStorage.setItem(
			L_PDP_PREV_COLLECTION_PAGE_KEY,
			JSON.stringify({
				url: `${window.location.pathname}${window.location.search}`,
				title: title || '',
			})
		)
	} catch (error) {
		/* sessionStorage unavailable */
	}
}

function l_getPdpPrevCollectionPage() {
	try {
		const raw = sessionStorage.getItem(L_PDP_PREV_COLLECTION_PAGE_KEY)
		return raw ? JSON.parse(raw) : null
	} catch (error) {
		return null
	}
}

function l_smoothScrollOnAnchorTags() {
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		const id = anchor.getAttribute('href').split('#')?.[1]
		if (!id) return

		anchor.addEventListener('click', (e) => {
			e.preventDefault()

			const target = document.querySelector(anchor.getAttribute('href'))
			target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
		})
	})
}

// =====================
// Focus Management & Trap Focus
// =====================

function isVisible(element) {
	if (!element) return false

	if (element.closest('[aria-hidden="true"], [hidden]')) return false

	const detailsParent = element.closest('details')
	if (detailsParent && !detailsParent.open && !detailsParent.contains(element.closest('summary'))) {
		return false
	}

	if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') {
		return false
	}

	const style = getComputedStyle(element)
	if (style.visibility === 'hidden' || style.visibility === 'collapse') return false

	if (element.getClientRects().length === 0) return false

	return true
}

function l_getFocusableElements(container) {
	const focusableSelectors = `summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe`

	return Array.from(container.querySelectorAll(focusableSelectors)).filter((el) => isVisible(el))
}

const l_focusTrapStack = []

function l_createFocusTrapLayer(container, defaultElementToFocus, enableFocusRange = true) {
	const layer = {
		container,
		defaultElementToFocus,
		enableFocusRange,
		_keydownAttached: false,
	}

	layer.handlers = {
		keydown(event) {
			const key = event.code?.toUpperCase()
			const isTab = key === 'TAB'
			const isArrowDown = key === 'ARROWDOWN'
			const isArrowUp = key === 'ARROWUP'

			const elements = l_getFocusableElements(layer.container)
			const [first, last] = [elements[0], elements[elements.length - 1]]
			const currentIndex = elements.indexOf(event.target)

			if (!elements.length) return

			if (isTab) {
				if ((event.target === last || last?.contains?.(event.target)) && !event.shiftKey) {
					event.preventDefault()
					first?.focus()
				} else if ((event.target === layer.container || first?.contains?.(event.target)) && event.shiftKey) {
					event.preventDefault()
					last?.focus()
				}
				return
			}

			if (isArrowDown) {
				event.preventDefault()
				if (currentIndex === -1 || currentIndex === elements.length - 1) {
					first?.focus()
				} else {
					elements[currentIndex + 1]?.focus()
				}
				return
			}

			if (isArrowUp) {
				event.preventDefault()
				if (currentIndex === -1 || currentIndex === 0) {
					last?.focus()
				} else {
					elements[currentIndex - 1]?.focus()
				}
			}
		},
		focusin(event) {
			if (!layer.container.contains(event.target)) return

			if (!layer._keydownAttached) {
				document.addEventListener('keydown', layer.handlers.keydown)
				layer._keydownAttached = true
			}
		},
		focusout() {
			if (!layer._keydownAttached) return

			document.removeEventListener('keydown', layer.handlers.keydown)
			layer._keydownAttached = false
		},
	}

	layer.activate = ({ focus = true } = {}) => {
		document.addEventListener('focusin', layer.handlers.focusin)
		document.addEventListener('focusout', layer.handlers.focusout)

		if (!focus) return

		const elements = l_getFocusableElements(layer.container)
		const elementToFocus = layer.defaultElementToFocus || elements[0]

		if (!elementToFocus) return

		elementToFocus.focus()

		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			layer.enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}

	layer.deactivate = () => {
		document.removeEventListener('focusin', layer.handlers.focusin)
		document.removeEventListener('focusout', layer.handlers.focusout)

		if (layer._keydownAttached) {
			document.removeEventListener('keydown', layer.handlers.keydown)
			layer._keydownAttached = false
		}
	}

	return layer
}

function l_shouldReplaceTrapLayer(topLayer, container) {
	if (!topLayer) return false
	if (topLayer.container === container) return true

	return topLayer.container.contains(container) || container.contains(topLayer.container)
}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
	const topLayer = l_focusTrapStack.at(-1)

	if (l_shouldReplaceTrapLayer(topLayer, container)) {
		topLayer.deactivate()
		l_focusTrapStack.pop()
	} else {
		topLayer?.deactivate()
	}

	const layer = l_createFocusTrapLayer(container, defaultElementToFocus, enableFocusRange)
	l_focusTrapStack.push(layer)
	layer.activate({ focus: true })
}

function l_removeTrapFocus(elementToFocus = null) {
	const topLayer = l_focusTrapStack.pop()
	topLayer?.deactivate()

	const previousLayer = l_focusTrapStack.at(-1)

	if (previousLayer) {
		previousLayer.activate({ focus: false })
	} else if (elementToFocus) {
		elementToFocus.focus()
	}
}

// =====================
// Scroll & Body Lock Utilities
// =====================

function l_lockBodyScroll(elementName) {
	// Add classes
	document.body.classList.add('js-block-page')

	if (elementName) {
		document.body.classList.add(`js-block-page-${elementName}`)
	}
}

function l_unLockBodyScroll(elementName) {
	// Remove classes
	if (elementName) {
		document.body.classList.remove(`js-block-page-${elementName}`)

		const isExistAnotherBlockedClass = Array.from(document.body.classList).some((cls) =>
			cls.startsWith('js-block-page-')
		)

		!isExistAnotherBlockedClass && document.body.classList.remove('js-block-page')
	} else {
		document.body.classList.remove('js-block-page')
	}
}

function l_setupVariablesBeforeOpenModal() {
	const themeHeights = window.theme?.readHeights?.() || {}
	const headerEl = document.querySelector('[data-header-height]')
	const announcementBarEl = document.querySelector('[data-announcement-bar]')
	const viewportHeight = visualViewport?.height ?? window.innerHeight
	const headerHeight =
		themeHeights.stickyHeaderHeight ||
		themeHeights.headerHeight ||
		themeHeights.headerInitialHeight ||
		headerEl?.offsetHeight ||
		0
	const announcementBarHeight = themeHeights.announcementBarHeight || announcementBarEl?.offsetHeight || 0

	// Mirror the theme's height calculation so LTA drawers/modals align with sticky header behavior.
	document.documentElement.style.setProperty('--l-viewport-height', `${viewportHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerHeight}px`)
	document.documentElement.style.setProperty('--l-announcement-bar-height', `${announcementBarHeight}px`)

	return {
		headerHeight,
		announcementBarHeight,
		headerTotalHeight: headerHeight + announcementBarHeight,
		viewportHeight,
	}
}

// =====================
// Media Controls
// =====================

function l_pauseAllMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.pauseVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.pause()
	})
}

function l_playMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.playVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"play"}', '*')
	})
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.play()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.play()
	})
}

// =====================
// Event/Utility Functions
// =====================

function l_debounce(fn, wait) {
	let t

	return function (...args) {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

const l_openKlaviyoForms = (formId = 'UMEyPP') => {
	window._klOnsite = window._klOnsite || []
	window._klOnsite.push(['openForm', formId])
}


window.addEventListener('DOMContentLoaded', () => {
  console.log("DOMContentLoaded");

  l_smoothScrollOnAnchorTags()
})


/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor(cartDrawer = null) {
		this.cartDrawer = cartDrawer
	}

	getRoutes() {
		return window.theme?.routes ?? {}
	}

	refreshCart() {
		document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }))
	}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		const formData = new FormData(formEl)

		config.body = formData

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			const prefix = `items[${index}]`

			formData.append(`${prefix}[id]`, item.id)
			formData.append(`${prefix}[quantity]`, item.quantity)
			if (item.selling_plan) {
				formData.append(`${prefix}[selling_plan]`, item.selling_plan)
			}

			// Add all other properties from the item object except id, quantity, and selling_plan
			Object.entries(item).forEach(([key, value]) => {
				if (['id', 'quantity', 'selling_plan'].includes(key)) return
				// If the value is an object, serialize it as JSON
				if (typeof value === 'object' && value !== null) {
					formData.append(`${prefix}[${key}]`, JSON.stringify(value))
				} else if (value !== undefined) {
					formData.append(`${prefix}[${key}]`, value)
				}
			})
		})

		config.body = formData
		return config
	}

	async addToCart(config, id = 'default') {
		const { cart_add_url: cartAddUrl } = this.getRoutes()

		if (!cartAddUrl) {
			return Promise.reject(new Error('LTACartServices: cart_add_url is not defined'))
		}

		const url = cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`

		// Note: Don't use finally / catch here
		return fetch(url, {
			...config,
			headers: {
				Accept: 'application/json',
				...config.headers,
			},
		})
			.then((res) => res.json())
			.then((data) => {
				if (data?.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))
        dispatchEvent(new CustomEvent('ocu:cart:open', { detail: { preventUpdate:false, triggerElement: document.activeElement } }))
				this.refreshCart()

				return data
			})
	}

	async changeCartLineItem(formData) {
		const { cart_change_url: cartChangeUrl } = this.getRoutes()

		if (!cartChangeUrl) {
			return Promise.reject(new Error('LTACartServices: cart_change_url is not defined'))
		}

		return fetch(cartChangeUrl, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
		this._onResize = this._onResize.bind(this)
		this._swiperInitialized = false
	}

	connectedCallback() {
		if (window?.Swiper) {
			this.setupCarousel()
			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._onResize)
		this.destroyCarousel()
	}

	destroyCarousel() {
		if (this.swiperCarousel && typeof this.swiperCarousel.destroy === 'function') {
			this.swiperCarousel.destroy(true, true)
		}

		this.swiperCarousel = undefined
		this._swiperInitialized = false
	}

	refresh() {
		this.destroyCarousel()

		if (window?.Swiper) {
			this.setupCarousel()
			return
		}

		document.addEventListener(
			'l:vendor:loaded',
			() => {
				this.setupCarousel()
			},
			{ once: true }
		)
	}

	setupCarousel() {
		if (!window?.Swiper || this._swiperInitialized) return

		const configKey = this.dataset.carouselConfig
		let configNode = null

		// Prefer the matching config for this carousel (avoids nested product-card configs)
		if (configKey) {
			configNode = this.querySelector(`[data-carousel-config="${configKey}"]`)
		}

		// Fall back to a direct-child config, then any descendant
		if (!configNode) {
			configNode = this.querySelector(':scope > [data-carousel-config]')
		}

		if (!configNode) {
			configNode = this.querySelector('[data-carousel-config]')
		}

		if (!configNode) return

		this.carouselConfig = JSON.parse(configNode.textContent)
		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
		this._swiperInitialized = true

		// Design mode scroll
		if (window.Shopify?.designMode) {
			this.designModeScroll()
		}

		// Set up navigation top position
		const hasNavigationTarget = this.carouselConfig?.navigation && this.carouselConfig.navigation.navigationHeightTarget
		if (hasNavigationTarget) {
			this.setupNavigation()
			window.addEventListener('resize', this._onResize)
		}

		// Dispatch event
		if (this.dataset.dispatchEvent === 'true') {
			this.dispatchEvent(
				new CustomEvent('l:swiper:carousel:loaded', {
					bubbles: true,
					detail: {
						id: this.id || this.dataset.id || 'default',
					},
				})
			)
		}
	}

	_onResize() {
		// Re-setup navigation when window resizes
		if (this._swiperInitialized && this.carouselConfig?.navigation?.navigationHeightTarget) {
			this.setupNavigation()
		}
	}

	setupNavigation() {
		const navigation = this.carouselConfig?.navigation
		if (!navigation || !navigation.navigationHeightTarget) return

		const targetEl = this.querySelector(navigation.navigationHeightTarget)
		if (!targetEl) return

		const navTop = `${targetEl.offsetHeight / 2}px`

		const nextNavEls = document.querySelectorAll(navigation.nextEl)
		const prevNavEls = document.querySelectorAll(navigation.prevEl)

		nextNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
		prevNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl === swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0, speed) {
		if (this.swiperCarousel && typeof this.swiperCarousel.slideTo === 'function') {
			this.swiperCarousel.slideTo(index, speed)
		}
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()

		this.boundClickHandler = this.handleClick.bind(this)
	}

	connectedCallback() {
		this.videoContainerEl = this.querySelector('[data-video-container]') || this

		if (this.dataset.videoPlayMode !== 'trigger_by_button') return

		this.addEventListener('click', this.boundClickHandler)
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.boundClickHandler)
		this.clearPlayMediaFallback()
	}

	handleClick(event) {
		const hasLoaded = this.classList.contains('is-loaded')
		const isPlaying = this.classList.contains('is-playing')

		hasLoaded ? (isPlaying ? this.pauseVideo() : this.playVideo()) : this.initPlayer(event)
	}

	initPlayer(event) {
		event.preventDefault()

		const templateEl = this.querySelector('template')

		if (!templateEl) return

		this.pauseOtherVideos()

		const templateContentEl = templateEl.content.cloneNode(true)

		this.videoContainerEl.appendChild(templateContentEl)
		this.classList.add('is-loaded')
		this.classList.remove('is-playing')
		this.classList.add('is-paused')

		this.playMediaWhenReady()
	}

	clearPlayMediaFallback() {
		if (!this._playFallbackTimer) return

		window.clearTimeout(this._playFallbackTimer)
		this._playFallbackTimer = 0
	}

	markPlaying() {
		this.classList.add('is-playing')
		this.classList.remove('is-paused')
	}

	markPaused() {
		this.classList.remove('is-playing')
		this.classList.add('is-paused')
	}

	playMediaWhenReady() {
		const iframeEl = this.videoContainerEl.querySelector('iframe')
		const videoEl = this.videoContainerEl.querySelector('video')

		if (iframeEl) {
			this.playIframeWhenReady(iframeEl)
			return
		}

		if (videoEl) {
			this.playNativeVideoWhenReady(videoEl)
			return
		}

		l_playMedia(this)
		this.markPlaying()
	}

	playIframeWhenReady(iframeEl) {
		let hasPlayed = false

		const playOnce = () => {
			if (hasPlayed) return

			hasPlayed = true
			this.clearPlayMediaFallback()
			l_playMedia(this)
			this.markPlaying()
		}

		iframeEl.addEventListener(
			'load',
			() => {
				window.setTimeout(playOnce, 200)
			},
			{ once: true }
		)

		this._playFallbackTimer = window.setTimeout(playOnce, 600)
	}

	playNativeVideoWhenReady(videoEl) {
		videoEl.addEventListener('playing', () => this.markPlaying(), { once: true })

		const play = () => l_playMedia(this)

		if (videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			play()
			return
		}

		videoEl.addEventListener('loadeddata', play, { once: true })
	}

	playVideo() {
		this.pauseOtherVideos()
		l_playMedia(this)

		this.markPlaying()
	}

	pauseVideo() {
		l_pauseAllMedia(this)

		this.markPaused()
	}

	pauseOtherVideos() {
		l_pauseAllMedia(this.parentElement)

		this.parentElement.querySelectorAll('l-media-video').forEach((videoEl) => {
			const isLoaded = videoEl.classList.contains('is-loaded')

			if (isLoaded) {
				videoEl.classList.remove('is-playing')
				videoEl.classList.add('is-paused')
			}
		})
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]:-DRAWER
 *****************************************/

class LTADrawer extends HTMLElement {
	static #pageInertStack = []
	static #inertElements = new Set()

	static #applyPageInert(activeDrawerEl) {
		LTADrawer.#inertElements.forEach((el) => {
			el.inert = false
		})
		LTADrawer.#inertElements.clear()

		if (!activeDrawerEl) return

		let current = activeDrawerEl

		while (current && current !== document.body && current.parentElement) {
			const parent = current.parentElement

			Array.from(parent.children).forEach((sibling) => {
				if (sibling === current) return

				sibling.inert = true
				LTADrawer.#inertElements.add(sibling)
			})

			current = parent
		}
	}

	static #pushPageInert(drawerEl) {
		const existingIndex = LTADrawer.#pageInertStack.indexOf(drawerEl)

		if (existingIndex !== -1) {
			LTADrawer.#pageInertStack.splice(existingIndex, 1)
		}

		LTADrawer.#pageInertStack.push(drawerEl)
		LTADrawer.#applyPageInert(drawerEl)
	}

	static #popPageInert(drawerEl) {
		const index = LTADrawer.#pageInertStack.lastIndexOf(drawerEl)

		if (index === -1) return

		LTADrawer.#pageInertStack.splice(index, 1)
		LTADrawer.#applyPageInert(LTADrawer.#pageInertStack.at(-1) ?? null)
	}

	constructor() {
		super()

		this.boundDrawerCloseOutsideOnClick = this.drawer_closeOutsideOnClick.bind(this)
		this.boundDrawerCloseOutsideOnEscape = this.drawer_closeOutsideOnEscape.bind(this)
		this.boundDrawerTriggerOnClick = this.drawer_triggerOnClick.bind(this)
		this.boundDrawerCloseOnClick = this.drawer_closeOnClick.bind(this)
		this.boundDrawerOpenHandler = this.drawer_onOtherDrawerOpen.bind(this)
	}

	connectedCallback() {
		this.setupDrawer()
	}

	disconnectedCallback() {
		this.drawer_destroy()
	}

	setupDrawer() {
		const showInBody = this.dataset.showInBody === 'true'

		// Move to body first; appendChild reconnects the element and runs setupDrawer again
		if (showInBody) {
			const mainContentEl = document.getElementById('main-content')

			if (mainContentEl?.contains(this)) {
				document.body.appendChild(this)
				return
			}
		}

		if (this._drawerInitialized) return

		const templateEl = this.querySelector('template')
		if (templateEl) {
			const templateContentEl = templateEl.content.cloneNode(true)
			this.appendChild(templateContentEl)
		}

		this.drawer_init()
		this._drawerInitialized = true
	}

	drawer_destroy() {
		if (this.classList.contains('is-active')) {
			this.drawer_close()
		}

		if (this.triggerSelector) {
			document.removeEventListener('click', this.boundDrawerTriggerOnClick)
		}

		this.removeEventListener('click', this.boundDrawerCloseOnClick)
		document.removeEventListener('l:drawer:open', this.boundDrawerOpenHandler)
		window.removeEventListener('click', this.boundDrawerCloseOutsideOnClick)
		window.removeEventListener('keyup', this.boundDrawerCloseOutsideOnEscape)

		this._drawerInitialized = false
	}

	drawer_init() {
		this.triggerSelector = this.dataset.triggerSelector
		this.closeSelector = this.dataset.closeSelector || ''
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', this.boundDrawerOpenHandler)
		}

		if (this.triggerSelector) {
			document.addEventListener('click', this.boundDrawerTriggerOnClick)
		}

		this.addEventListener('click', this.boundDrawerCloseOnClick)
		this.drawer_syncTriggerA11y(false)
	}

	drawer_getTriggerEls() {
		if (!this.triggerSelector) return []

		return Array.from(document.querySelectorAll(this.triggerSelector))
	}

	drawer_syncTriggerA11y(isExpanded) {
		this.drawer_getTriggerEls().forEach((triggerEl) => {
			if (this.id && !triggerEl.hasAttribute('aria-controls')) {
				triggerEl.setAttribute('aria-controls', this.id)
			}

			if (!triggerEl.hasAttribute('aria-haspopup')) {
				triggerEl.setAttribute('aria-haspopup', 'dialog')
			}

			triggerEl.setAttribute('aria-expanded', String(isExpanded))
		})
	}

	drawer_setDialogA11y(isOpen) {
		if (isOpen) {
			this.removeAttribute('aria-hidden')
			this.removeAttribute('tabindex')
		} else {
			this.setAttribute('aria-hidden', 'true')
			this.setAttribute('tabindex', '-1')
		}
	}

	drawer_lockPageInert() {
		LTADrawer.#pushPageInert(this)
	}

	drawer_unlockPageInert() {
		LTADrawer.#popPageInert(this)
	}

	drawer_triggerOnClick(event) {
		const triggerEl = event.target.closest(this.triggerSelector)
		if (!triggerEl) return

		event.preventDefault()
		this.drawer_open(triggerEl)
	}

	drawer_closeOnClick(event) {
		const internalCloseEl = event.target.closest('[data-close]')

		if (internalCloseEl && this.contains(internalCloseEl)) {
			this.drawer_close()
			return
		}

		if (this.closeSelector && event.target.closest(this.closeSelector)) {
			this.drawer_close()
		}
	}

	drawer_onOtherDrawerOpen(event) {
		if (event.detail.drawerName !== this.drawerName) {
			this.drawer_close()
		}
	}

	drawer_open(triggerEl) {
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			l_trapFocus(this.drawerInnerEl, this.defaultElementToFocus)
		}

		// Set up accessibility
		this.drawer_setDialogA11y(true)
		this.drawer_syncTriggerA11y(true)
		this.drawer_lockPageInert()

		// Set global events using bound handlers
		window.addEventListener('click', this.boundDrawerCloseOutsideOnClick)
		window.addEventListener('keyup', this.boundDrawerCloseOutsideOnEscape)

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		l_unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove accessibility
		this.drawer_setDialogA11y(false)
		this.drawer_syncTriggerA11y(false)
		this.drawer_unlockPageInert()

		// Remove global events using the same bound handlers
		window.removeEventListener('click', this.boundDrawerCloseOutsideOnClick)
		window.removeEventListener('keyup', this.boundDrawerCloseOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		if (!this.classList.contains('is-active')) return

		const target = event.target
		const triggerSelector = this.dataset.drawerTriggerSelector || 'l-drawer-trigger'
		const isDrawerTrigger = target.closest(triggerSelector)

		const isTriggerClick = this.triggerSelector && target.closest(this.triggerSelector)
		const isOutsideClick = !this.drawerInnerEl.contains(target) && !isTriggerClick && !isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (!this.classList.contains('is-active')) return

		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTADrawerTrigger extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.drawerEl = document.querySelector(this.dataset.selector)

		// Events
		if (this.drawerEl) {
			this.addEventListener('click', () => {
				this.drawerEl?.drawer_open?.(this)
			})
		}
	}
}

customElements.define('l-drawer-trigger', LTADrawerTrigger)

/*****************************************
 * [COMPONENT]: QUANTITY INPUT
 ******************************************/

if (!customElements.get('l-qty-input')) {
	class LTAQtyInput extends HTMLElement {
		constructor() {
			super()

			this.onButtonClick = this.onButtonClick.bind(this)
			this.onInputChange = this.onInputChange.bind(this)
			this.onInputBeforeChange = this.onInputBeforeChange.bind(this)
		}

		connectedCallback() {
			this.input = this.querySelector('input')
			this.buttonMinus = this.querySelector("button[name='minus']")
			this.buttonPlus = this.querySelector("button[name='plus']")

			if (!this.input) return

			this.changeEvent = new Event('change', { bubbles: true })

			this.validateQtyRules()
			this.setupEventListeners()
		}

		setupEventListeners() {
			this.input.addEventListener('input', this.onInputBeforeChange)
			this.input.addEventListener('change', this.onInputChange)
			this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick))
		}

		onInputBeforeChange() {
			const value = parseInt(this.input.value)

			if (isNaN(value)) return

			const min = parseInt(this.input.min)
			const max = parseInt(this.input.max)
			let newValue = value

			if (!isNaN(min) && value < min) {
				newValue = min
			}

			if (!isNaN(max) && value > max) {
				newValue = max
			}

			if (newValue !== value) {
				this.input.value = newValue
			}
		}

		onInputChange() {
			this.validateQtyRules()
		}

		onButtonClick(event) {
			event.preventDefault()

			const previousValue = this.input.value
			const buttonName = event.currentTarget.name

			if (buttonName === 'plus') {
				this.incrementQuantity()
			} else if (buttonName === 'minus') {
				this.decrementQuantity()
			}

			if (previousValue !== this.input.value) {
				this.input.dispatchEvent(this.changeEvent)
				this.onQuantityChanged(previousValue, parseInt(this.input.value))
			}
		}

		onQuantityChanged(oldValue, newValue) {
			// Override in child classes if needed
		}

		incrementQuantity() {
			const min = parseInt(this.input.min, 10)
			const step = parseInt(this.input.step, 10)
			const value = parseInt(this.input.value, 10)

			if (!isNaN(min) && !isNaN(step) && min > step && value === 0) {
				this.input.value = min
			} else {
				this.input.stepUp()
			}
		}

		decrementQuantity() {
			this.input.stepDown()

			const min = this.input.min
			if (min && min === this.input.value) {
				this.input.value = parseInt(this.input.min, 10)
			}
		}

		validateQtyRules() {
			const value = parseInt(this.input.value)

			if (this.input.min && this.buttonMinus) {
				this.buttonMinus.toggleAttribute('disabled', value <= parseInt(this.input.min))
			}

			if (this.input.max && this.buttonPlus) {
				this.buttonPlus.toggleAttribute('disabled', value >= parseInt(this.input.max))
			}
		}

		disconnectedCallback() {
			if (this.input) {
				this.input.removeEventListener('input', this.onInputBeforeChange)
				this.input.removeEventListener('change', this.onInputChange)
			}

			this.querySelectorAll('button').forEach((button) => button.removeEventListener('click', this.onButtonClick))
		}
	}

	customElements.define('l-qty-input', LTAQtyInput)
}

/*****************************************
 * [COMPONENT]: SIZE GENDER TABS
 ******************************************/

if (!customElements.get('size-gender-tabs')) {
	const sizeGenderTabState = new Map()

	function getSizeGenderTabKey(element) {
		const productInfo = element.closest('product-info')
		const optionWrapper = element.closest('[data-option-position]')

		if (!productInfo || !optionWrapper) return null

		// Keyed by section (not product) so the tab survives switching between combined-product colors
		return `${productInfo.dataset.section || productInfo.dataset.productId}-${optionWrapper.dataset.optionPosition}`
	}

	class SizeGenderTabs extends HTMLElement {
		connectedCallback() {
			this.triggers = Array.from(this.querySelectorAll('[data-size-gender-trigger]'))
			this.selectedValueEl = this.querySelector('[data-size-gender-selected-value]')

			if (!this.triggers.length) return

			this.onClick = this.onClick.bind(this)
			this.onKeydown = this.onKeydown.bind(this)

			this.triggers.forEach((trigger) => {
				trigger.addEventListener('click', this.onClick)
				trigger.addEventListener('keydown', this.onKeydown)
			})

			const savedGender = sizeGenderTabState.get(getSizeGenderTabKey(this))
			if (savedGender) {
				this.select(savedGender)
			} else {
				this.updateSelectedSize()
			}
		}

		disconnectedCallback() {
			if (!this.triggers) return

			const tabKey = getSizeGenderTabKey(this)
			if (tabKey && this.dataset.sizeGender) {
				sizeGenderTabState.set(tabKey, this.dataset.sizeGender)
			}

			this.triggers.forEach((trigger) => {
				trigger.removeEventListener('click', this.onClick)
				trigger.removeEventListener('keydown', this.onKeydown)
			})
		}

		onClick(event) {
			this.select(event.currentTarget.dataset.sizeGenderTrigger)
		}

		onKeydown(event) {
			if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

			event.preventDefault()

			const currentIndex = this.triggers.indexOf(event.currentTarget)
			const lastIndex = this.triggers.length - 1
			let nextIndex

			if (event.key === 'ArrowRight') {
				nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
			} else {
				nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
			}

			const nextTrigger = this.triggers[nextIndex]
			this.select(nextTrigger.dataset.sizeGenderTrigger)
			nextTrigger.focus()
		}

		select(gender) {
			if (!gender || this.dataset.sizeGender === gender) return

			this.dataset.sizeGender = gender

			const tabKey = getSizeGenderTabKey(this)
			if (tabKey) {
				sizeGenderTabState.set(tabKey, gender)
			}

			this.triggers.forEach((trigger) => {
				const isActive = trigger.dataset.sizeGenderTrigger === gender
				trigger.classList.toggle('is-active', isActive)
				trigger.setAttribute('aria-selected', isActive ? 'true' : 'false')
			})

			this.updateSelectedSize()
		}

		updateSelectedSize() {
			if (!this.selectedValueEl) {
				this.selectedValueEl = this.querySelector('[data-size-gender-selected-value]')
			}
			if (!this.selectedValueEl) return

			const checked = this.querySelector('input[data-single-option-selector]:checked')
			if (!checked) {
				this.selectedValueEl.textContent = ''
				return
			}

			const label = checked.id ? this.querySelector(`label[for="${CSS.escape(checked.id)}"]`) : null
			const menEl = label?.querySelector('[data-gender="men"]')
			const womenEl = label?.querySelector('[data-gender="women"]')
			const menSize = (menEl?.dataset.size || menEl?.textContent || '').trim()
			const womenSize = (womenEl?.dataset.size || womenEl?.textContent || '').trim()

			if (menSize && womenSize) {
				this.selectedValueEl.textContent = `Men ${menSize} / Women ${womenSize}`
				return
			}

			this.selectedValueEl.textContent = menSize || womenSize || ''
		}
	}

	customElements.define('size-gender-tabs', SizeGenderTabs)
}

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

class LTACTAButton extends HTMLElement {
	constructor() {
		super()

		this.handleAtcBtnClick = this.handleAtcBtnClick.bind(this)
		this.handleQtyChange = this.handleQtyChange.bind(this)
	}

	connectedCallback() {
		this.buttonEl = this.querySelector('[data-atc-trigger], .l-cPCard__cta-button')
		this.qtyInputEl = this.querySelector('[data-qty-input]')

		if (this.qtyInputEl) {
			this.qtyInputEl.addEventListener('change', this.handleQtyChange)
			this.syncQuantityFromInput()
		}

		if (this.buttonEl) {
			this.buttonEl.addEventListener('click', this.handleAtcBtnClick)
		}
	}

	disconnectedCallback() {
		if (this.qtyInputEl) {
			this.qtyInputEl.removeEventListener('change', this.handleQtyChange)
		}

		if (this.buttonEl) {
			this.buttonEl.removeEventListener('click', this.handleAtcBtnClick)
		}
	}

	syncQuantityFromInput() {
		if (!this.qtyInputEl) return

		const qty = parseInt(this.qtyInputEl.value, 10)

		if (!isNaN(qty)) {
			this.dataset.quantity = String(qty)
		}
	}

	handleQtyChange() {
		this.syncQuantityFromInput()
	}

	handleAtcBtnClick() {
		if (!this.buttonEl || this.buttonEl.disabled) return

		const usesThemeBtnLoader = this.buttonEl.classList.contains('btn')

		this.buttonEl.classList.add('is-loading')
		if (usesThemeBtnLoader) {
			this.buttonEl.setAttribute('disabled', 'disabled')
		} else {
			this.buttonEl.classList.add('l-button-loading')
		}

		const { variantId, quantity, sellingPlanId } = this.dataset
		const config = {
			id: variantId,
			quantity: parseInt(quantity),
			...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
		}

		const formData = ltaCartService.createConfigFromItems([config])

		ltaCartService
			.addToCart(formData, 'l-atc-button')
			.catch(console.error)
			.finally(() => {
				this.buttonEl.classList.remove('is-loading', 'l-button-loading')

				if (usesThemeBtnLoader) {
					this.buttonEl.removeAttribute('disabled')
				}
			})
	}
}

customElements.define('l-atc-button', LTACTAButton)

/*****************************************
 * [COMPONENT]: VARIANT PICKER
 ******************************************/

if (!customElements.get('l-drawer-variant-picker')) {
	class LTADrawerVariantPicker extends HTMLElement {
		constructor() {
			super()

			this._onDocumentClick = this._onDocumentClick.bind(this)
			this._onProductAdded = this._onProductAdded.bind(this)
		}

		connectedCallback() {
			this._drawerEl = this.querySelector('l-drawer')
			this._innerEl = this.querySelector('[data-variant-picker-inner]')
			this._mountEl = this.querySelector('[data-variant-picker-mount]')
			this._loaderEl = this.querySelector('[data-variant-picker-loader]')
			this._productNameEl = this.querySelector('[data-variant-picker-product-name]')

			document.addEventListener('click', this._onDocumentClick)
			document.addEventListener('l:product:added', this._onProductAdded)
		}

		disconnectedCallback() {
			document.removeEventListener('click', this._onDocumentClick)
			document.removeEventListener('l:product:added', this._onProductAdded)
		}

		_onDocumentClick(event) {
			const triggerEl = event.target.closest('[data-variant-picker-open]')

			if (!triggerEl) return

			event.preventDefault()
			event.stopPropagation()

			const productHandle = triggerEl.dataset.productHandle

			if (!productHandle) return

			this.open(productHandle, triggerEl)
		}

		_onProductAdded(event) {
			if (event.detail?.id !== 'l-atc-button') return

			this._drawerEl?.drawer_close?.()
		}

		async open(productHandle, triggerEl) {
			if (!this._drawerEl || !this._mountEl) return

			this._triggerEl = triggerEl
			this._mountEl.innerHTML = ''
			this._setLoading(true)
			this._setProductName('')

			this._drawerEl.drawer_open(triggerEl)

			try {
				const [, response] = await Promise.all([
					this._ensureProductStyles(),
					fetch(`/products/${productHandle}?section_id=a-variant-picker-popup`),
				])
				const html = await response.text()
				const doc = new DOMParser().parseFromString(html, 'text/html')
				const contentEl = doc.querySelector('[data-variant-picker-form]')

				if (!contentEl) {
					throw new Error('Variant picker content not found')
				}

				this._mountEl.innerHTML = contentEl.outerHTML
				const mountedEl = this._mountEl.querySelector('[data-variant-picker-form]')

				if (!mountedEl) {
					throw new Error('Variant picker content not found')
				}

				this._setProductName(mountedEl.dataset.productTitle || '')
				this._initForm(mountedEl)
			} catch (error) {
				console.error(error)
				this._mountEl.innerHTML = ''
				this._setProductName('')
			} finally {
				this._setLoading(false)
			}
		}

		_ensureProductStyles() {
			const href = this.dataset.productStylesHref

			if (!href) return Promise.resolve()

			const existingLink = document.querySelector(`link[href="${href}"]`)

			if (existingLink) {
				if (existingLink.sheet || existingLink.dataset.loaded === 'true') {
					return Promise.resolve()
				}

				return new Promise((resolve) => {
					existingLink.addEventListener('load', () => resolve(), { once: true })
					existingLink.addEventListener('error', () => resolve(), { once: true })
				})
			}

			return new Promise((resolve) => {
				const linkEl = document.createElement('link')
				linkEl.rel = 'stylesheet'
				linkEl.href = href
				linkEl.onload = () => {
					linkEl.dataset.loaded = 'true'
					resolve()
				}
				linkEl.onerror = () => resolve()
				document.head.appendChild(linkEl)
			})
		}

		_setLoading(isLoading) {
			this._setElementHidden(this._loaderEl, !isLoading)
			this._setElementHidden(this._mountEl, isLoading)

			if (this._innerEl) {
				this._innerEl.setAttribute('aria-busy', isLoading ? 'true' : 'false')
			}
		}

		_setElementHidden(el, isHidden) {
			if (!el) return

			el.hidden = isHidden
			el.classList.toggle('l-hidden', isHidden)
		}

		_setProductName(title) {
			if (!this._productNameEl) return

			this._productNameEl.textContent = title
		}

		_initForm(contentEl) {
			const variants = JSON.parse(contentEl.querySelector('[data-variant-picker-variants]')?.textContent || '[]')
			const variantSelectsEl = contentEl.querySelector('variant-selects')
			const atcEl = contentEl.querySelector('[data-variant-picker-atc]')
			const atcButtonEl = contentEl.querySelector('[data-atc-trigger]')
			const atcTextEl = atcButtonEl?.querySelector('[data-add-to-cart-text]')

			const getSelectedOptions = () => {
				if (!variantSelectsEl) return []

				return Array.from(variantSelectsEl.querySelectorAll('fieldset input:checked'))
					.sort((a, b) => {
						const posA = parseInt(String(a.dataset.index || '').replace('option', ''), 10)
						const posB = parseInt(String(b.dataset.index || '').replace('option', ''), 10)

						return posA - posB
					})
					.map((input) => input.value)
			}

			const findVariant = () =>
				variants.find((variant) => variant.options.every((option, index) => option === getSelectedOptions()[index]))

			const updateVariant = (variant) => {
				if (!variant) return

				if (atcEl) {
					atcEl.dataset.variantId = String(variant.id)
				}

				if (atcButtonEl) {
					atcButtonEl.disabled = !variant.available
				}

				const label = variant.available
					? atcButtonEl?.dataset.labelAvailable || atcTextEl?.textContent || atcButtonEl?.textContent
					: atcButtonEl?.dataset.labelSoldOut || 'Sold out'

				if (atcTextEl) {
					atcTextEl.textContent = label
				} else if (atcButtonEl) {
					atcButtonEl.textContent = label
				}
			}

			if (variantSelectsEl) {
				variantSelectsEl.addEventListener('change', () => {
					updateVariant(findVariant())
				})
			}

			updateVariant(findVariant())
		}
	}

	customElements.define('l-drawer-variant-picker', LTADrawerVariantPicker)
}

if (!customElements.get('l-sold-out-state')) {
	class LTASoldOutState extends HTMLElement {
		connectedCallback() {
			this._pending = false
			this._formEl = this.closest('product-form') || this.closest('[data-form-wrapper]')
			this._loadingEl = this.querySelector('[data-sold-out-loading]')
			this._soldOutEl = this.querySelector('[data-sold-out-button]')
			this._bisEl = this.querySelector('[data-back-in-stock-button]')
			this._bisTriggerEl = this._bisEl?.querySelector('button')
			this._onOptionChange = ({ data }) => this._handleOptionChange(data)
			this._onVariantChange = ({ data }) => this._handleVariantChange(data)
			this._onBisClick = () => this._openKlaviyoForm()

			if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
				if (window.theme.PUB_SUB_EVENTS.optionValueSelectionChange) {
					this._unsubOption = subscribe(
						window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
						this._onOptionChange
					)
				}

				if (window.theme.PUB_SUB_EVENTS.variantChange) {
					this._unsubVariant = subscribe(window.theme.PUB_SUB_EVENTS.variantChange, this._onVariantChange)
				}
			}

			// Unavailable combos set variant--unavailable without publishing variantChange
			if (this._formEl) {
				this._classObserver = new MutationObserver(() => this._syncFromForm())
				this._classObserver.observe(this._formEl, { attributes: true, attributeFilter: ['class'] })
			}

			this._bisTriggerEl?.addEventListener('click', this._onBisClick)
		}

		disconnectedCallback() {
			this._unsubOption?.()
			this._unsubOption = null
			this._unsubVariant?.()
			this._unsubVariant = null
			this._classObserver?.disconnect()
			this._classObserver = null
			this._bisTriggerEl?.removeEventListener('click', this._onBisClick)
		}

		_openKlaviyoForm() {
			if (this._pending) return

			const formId = this._bisTriggerEl?.dataset.klaviyoFormId || 'Rv8KfL'
			l_openKlaviyoForms(formId)
		}

		_handleOptionChange(data) {
			const productInfo = this.closest('product-info')
			if (!productInfo) return

			const target = data?.event?.target || data?.target
			if (target && !productInfo.contains(target)) return

			const wasShowingAtc = !this._formEl?.hasAttribute('data-sold-out-ui')

			this._setPending(true)

			// Leaving ATC: show loading dots immediately so there's no blank gap
			if (wasShowingAtc) {
				this._showPendingLoadingPlaceholder()
			}
		}

		_showPendingLoadingPlaceholder() {
			if (!this._formEl) return

			this._formEl.setAttribute('data-sold-out-ui', 'true')

			if (this._loadingEl) this._loadingEl.hidden = false
			if (this._soldOutEl) this._soldOutEl.hidden = true
			if (this._bisEl) this._bisEl.hidden = true
		}

		_handleVariantChange(data) {
			const productInfo = this.closest('product-info')
			if (!productInfo) return

			const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section
			if (data?.sectionId && sectionId && data.sectionId !== sectionId) return

			if (!data?.variant) {
				this._applyState(false, false)
				this._setPending(false)
				return
			}

			const backInStock = this._readBackInStockFromHtml(data.html, productInfo)
			this._applyState(data.variant.available === false, backInStock)
			this._setPending(false)
		}

		_readBackInStockFromHtml(html, productInfo) {
			if (!html) {
				return this._formEl?.getAttribute('data-back-in-stock') === 'true'
			}

			const sourceInfo = productInfo?.id ? html.getElementById(productInfo.id) : html.querySelector('product-info')
			const sourceForm = sourceInfo?.querySelector('product-form[data-back-in-stock], [data-form-wrapper][data-back-in-stock]')

			if (sourceForm) {
				return sourceForm.getAttribute('data-back-in-stock') === 'true'
			}

			return this._formEl?.getAttribute('data-back-in-stock') === 'true'
		}

		_setPending(isPending) {
			this._pending = Boolean(isPending)
			this.classList.toggle('is-pending', this._pending)
			this._formEl?.classList.toggle('is-sold-out-pending', this._pending)

			if (this._bisTriggerEl) {
				this._bisTriggerEl.disabled = this._pending
			}

			const atcButton = this._formEl?.querySelector('[data-add-to-cart]')
			if (!atcButton) return

			if (this._pending) {
				this._atcDisabledBeforePending = atcButton.disabled
				atcButton.disabled = true
			} else if (!this._formEl?.hasAttribute('data-sold-out-ui')) {
				atcButton.disabled = Boolean(this._atcDisabledBeforePending)
			}
		}

		_syncFromForm() {
			if (!this._formEl) return

			const isUnavailable = this._formEl.classList.contains('variant--unavailable')

			// Fetch finished with an invalid combo — clear pending and hide both
			if (isUnavailable) {
				this._applyState(false, false)
				this._setPending(false)
				return
			}

			// Keep the current button visible while section HTML is fetching
			if (this._pending) return

			const isSoldOut = this._formEl.classList.contains('variant--soldout')
			const backInStock = this._formEl.getAttribute('data-back-in-stock') === 'true'
			this._applyState(isSoldOut, backInStock)
		}

		_applyState(isSoldOut = false, backInStock = false) {
			if (this._formEl) {
				this._formEl.setAttribute('data-back-in-stock', backInStock ? 'true' : 'false')

				if (isSoldOut) {
					this._formEl.setAttribute('data-sold-out-ui', 'true')
				} else {
					this._formEl.removeAttribute('data-sold-out-ui')
				}
			}

			const showSoldOutButton = Boolean(isSoldOut) && !backInStock
			const showBisButton = Boolean(isSoldOut) && backInStock

			if (this._loadingEl) {
				this._loadingEl.hidden = true
			}

			if (this._soldOutEl) {
				this._soldOutEl.hidden = !showSoldOutButton
			}

			if (this._bisEl) {
				this._bisEl.hidden = !showBisButton
			}
		}
	}

	customElements.define('l-sold-out-state', LTASoldOutState)
}
